/**
 * Что ученик купил и до какого дня.
 *
 * В документе ученика:
 *   access       'reading' — только курс чтения, на срок;
 *                'lessons' — только уроки, курс закрыт;
 *                'both'    — уроки и курс, без срока;
 *                'sheets'  — купил только шпаргалки, курс закрыт.
 *                Нет поля — старый ученик, ведёт себя как 'both'.
 *   accessStart  'ГГГГ-ММ-ДД' — с какого дня открыт курс (только для 'reading');
 *   accessUntil  'ГГГГ-ММ-ДД' — последний день доступа (только для 'reading');
 *   createdAt    ISO-строка — когда ученика завели в админке.
 *
 * Срок проверяется в трёх местах: при входе на сервере (functions/index.js →
 * authLogin), при входе в браузере и при открытии курса — чтобы ученик, который
 * вошёл раньше и не выходил, тоже упёрся в конец срока.
 */
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, authReady } from '../firebase';

export const ACCESS_TYPES = [
  { value: 'reading', label: 'Только курс чтения', short: 'Курс чтения' },
  { value: 'lessons', label: 'Только уроки', short: 'Только уроки' },
  { value: 'both', label: 'Уроки и курс чтения', short: 'Уроки + курс' },
  { value: 'sheets', label: 'Только шпаргалки', short: 'Шпаргалки' },
];
// У кого из них курса чтения нет.
const NO_COURSE = ['lessons', 'sheets'];

export const accessOf = (student) =>
  ACCESS_TYPES.some((t) => t.value === student?.access) ? student.access : 'both';

export const accessLabel = (student) =>
  ACCESS_TYPES.find((t) => t.value === accessOf(student)).label;

const pad = (n) => String(n).padStart(2, '0');

export const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => toISODate(new Date());

export function parseISODate(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(String(s || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** Тот же день через год; 29 февраля становится 28-м. */
export function addYear(s) {
  const d = parseISODate(s) || new Date();
  const y = new Date(d.getFullYear() + 1, d.getMonth(), d.getDate());
  if (y.getMonth() !== d.getMonth()) y.setDate(0);
  return toISODate(y);
}

/** Дата из чего угодно, что могло лечь в базу: строка, число, Timestamp. */
export function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(v) {
  const d = typeof v === 'string' && parseISODate(v) ? parseISODate(v) : toDate(v);
  return d ? `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}` : '—';
}

/** Момент, когда доступ закрывается: полночь после последнего дня. */
export function accessEnd(student) {
  if (accessOf(student) !== 'reading') return null;
  const last = parseISODate(student.accessUntil);
  return last ? new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1) : null;
}

export function isExpired(student, now = new Date()) {
  const end = accessEnd(student);
  return Boolean(end && now >= end);
}

/** Можно ли сейчас открыть курс чтения. */
export const canOpenCourse = (student, now = new Date()) =>
  !NO_COURSE.includes(accessOf(student)) && !isExpired(student, now);

/** Почему нельзя: 'expired' | 'no-course' | null. */
export function courseBlockReason(student, now = new Date()) {
  if (!student) return null;
  if (NO_COURSE.includes(accessOf(student))) return 'no-course';
  if (isExpired(student, now)) return 'expired';
  return null;
}

export function msLeft(student, now = new Date()) {
  const end = accessEnd(student);
  return end ? end.getTime() - now.getTime() : null;
}

/** Сколько дней осталось, считая сегодняшний. null — срока нет. */
export function daysLeft(student, now = new Date()) {
  const ms = msLeft(student, now);
  return ms === null ? null : Math.max(0, Math.ceil(ms / 86400000));
}

/** Сколько процентов срока уже прошло — для полоски. */
export function elapsedShare(student, now = new Date()) {
  const start = parseISODate(student?.accessStart);
  const end = accessEnd(student);
  if (!start || !end || end <= start) return null;
  return Math.min(1, Math.max(0, (now - start) / (end - start)));
}

/**
 * Документ ученика для проверки доступа — с ограничением по времени, чтобы
 * вход не завис, если база не отвечает. null — ученика нет; ошибка — нет связи.
 */
export async function fetchStudentAccess(id, ms = 6000) {
  const wait = (t) => new Promise((resolve) => setTimeout(resolve, t));
  await Promise.race([authReady, wait(3000)]);
  const snap = await Promise.race([
    getDoc(doc(db, 'students', id)),
    wait(ms).then(() => { throw new Error('timeout'); }),
  ]);
  return snap.exists() ? snap.data() : null;
}

/* ---------- одинаковые имена и логины ---------- */

export const normalizeName = (s) => String(s || '').trim().replace(/\s+/gu, ' ').toLocaleLowerCase('ru');
export const normalizeLogin = (s) => String(s || '').trim().toLocaleLowerCase('ru');

/** Свежий список из базы, а не из памяти страницы: её могли открыть час назад. */
async function liveStudents() {
  const snap = await getDocs(collection(db, 'students'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => !s.deleted);
}

/** Другой ученик с тем же именем (без учёта регистра и лишних пробелов) или null. */
export async function findDuplicateName(name, excludeId = null) {
  const n = normalizeName(name);
  if (!n) return null;
  return (await liveStudents()).find((s) => s.id !== excludeId && normalizeName(s.name) === n) || null;
}

/** Другой ученик с тем же логином (без учёта регистра) или null. */
export async function findDuplicateLogin(username, excludeId = null) {
  const n = normalizeLogin(username);
  if (!n) return null;
  return (await liveStudents()).find((s) => s.id !== excludeId && normalizeLogin(s.username) === n) || null;
}

/** Группы учеников, у которых совпадает ключ: [[a, b], [c, d, e]]. */
export function duplicateGroups(students, keyFn) {
  const map = new Map();
  students.forEach((s) => {
    const k = keyFn(s);
    if (!k) return;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(s);
  });
  return [...map.values()].filter((g) => g.length > 1);
}
